import { c as _c } from "react/compiler-runtime";
import { OutcomeIdentityImage } from '@/components/market/identity/outcome-identity-image';
import type { OutcomeHueName } from '@/hooks/chart/use-outcome-color';
import { cn } from '@/lib/ui/utils';
const FALLBACK_EMOJI = '\u{1F4A1}';
type Size = 'md' | 'sm';
interface ThesisMarketAvatarProps {
  marketPubkey: string;
  outcomeIndex: number;
  outcomeTitle: string;
  outcomeImageUrl: string | null;
  outcomeImageNsfw: boolean;
  categoryEmoji?: string;
  hue: OutcomeHueName | undefined;
  size?: Size;
  className?: string;
}
const SIZE_CLASS: Record<Size, string> = {
  md: 'size-10',
  sm: 'size-9'
};

/**
 * Rounded-square market image with an outcome-colored status dot in the
 * bottom-right corner. Shared between `PositionRow` (portfolio positions) and
 * the portfolio variant of `ThesisCard` so the two surfaces stay visually
 * consistent.
 *
 * Behaviour parity with the previous in-line `OutcomeThumbnail` block: NSFW or
 * missing image falls back to the category emoji.
 */
export function ThesisMarketAvatar(t0) {
  const $ = _c(20);
  const {
    marketPubkey,
    outcomeIndex,
    outcomeTitle,
    outcomeImageUrl,
    outcomeImageNsfw,
    categoryEmoji: t1,
    hue,
    size: t2,
    className
  } = t0;
  const categoryEmoji = t1 === undefined ? FALLBACK_EMOJI : t1;
  const size = t2 === undefined ? "md" : t2;
  const hasImage = outcomeImageUrl !== null && outcomeImageUrl !== "" && !outcomeImageNsfw;
  const t3 = SIZE_CLASS[size];
  let t4;
  if ($[0] !== className || $[1] !== t3) {
    t4 = cn("relative shrink-0", t3, className);
    $[0] = className;
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== categoryEmoji || $[4] !== hasImage || $[5] !== hue || $[6] !== marketPubkey || $[7] !== outcomeImageNsfw || $[8] !== outcomeImageUrl || $[9] !== outcomeIndex || $[10] !== outcomeTitle) {
    t5 = <div className="size-full overflow-hidden rounded-control bg-background-tertiary">{hasImage ? <OutcomeIdentityImage variant="thumbnail" entity={{
        kind: "outcome",
        marketPubkey,
        outcomeIndex,
        hue,
        imageUrl: outcomeImageUrl,
        nsfw: outcomeImageNsfw
      }} alt={outcomeTitle} size="size-full" rounded="rounded-control" preview={false} /> : <span aria-hidden={true} data-testid="thesis-market-avatar-fallback" className="absolute inset-0 flex items-center justify-center text-2xl leading-none">{categoryEmoji}</span>}</div>;
    $[3] = categoryEmoji;
    $[4] = hasImage;
    $[5] = hue;
    $[6] = marketPubkey;
    $[7] = outcomeImageNsfw;
    $[8] = outcomeImageUrl;
    $[9] = outcomeIndex;
    $[10] = outcomeTitle;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = hue === undefined ? "bg-background-tertiary" : `bg-outcome-${hue}-cta`;
  let t7;
  if ($[12] !== t6) {
    t7 = cn("absolute -right-1 -bottom-1 size-3 rounded-full border-2 border-background-secondary", t6);
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t7) {
    t8 = <span aria-hidden={true} className={t7} />;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t4 || $[17] !== t5 || $[18] !== t8) {
    t9 = <div className={t4}>{t5}{t8}</div>;
    $[16] = t4;
    $[17] = t5;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  return t9;
}